import { concat, defer, Observable, of } from "rxjs"
import { catchError, delayWhen, filter, map, switchMap } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import type { EpicDependencies } from "../../../../rxjs/configureEpics"
import { getHttpHeaders } from "../../../../auth/selectors"
import {
    ITEMS_CATEGORIES_AGENDA_WILL_MOUNT,
    itemsAndCategoriesListDataLoaded,
    itemsAndCategoriesListOverrideDataLoaded,
    ITEMS_AND_CATEGORIES_LIST_REQUESTED,
    MENU_DATA_INVALIDATED,
    shouldRefetchMenuDataEntity,
} from "../../actions"
import { createLoadingEnded, createLoadingEndedError, createLoadingStarted } from "../../../../filters/actions"
import { fetchItemsAndCategoriesListData } from "./request"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../../../auth/actions"
import { shouldDownloadItemsAndCategoriesData, shouldDownloadLocationOverrideData } from "../../selectors"
import { SEND_CSV_FILE_SUCCESS } from "../../../../../api/actions"
import { isSchematizedAgendaOpened } from "../../../../data/selectors"
import { getSelectedLocationId, getSelectedLocationOverrideId } from "../../../../common/locationsPicker/selectors"
import { LOCATION_OVERRIDE_SELECTED, LOCATION_SELECTED } from "../../../../common/locationsPicker/actions"
import { Action, StateObject } from "../../../../redux/types"
import { waitForGlobalFilterSet } from "../../../reports/customers/customersTable/epics"

export const startLoadingOnMountEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(
            ITEMS_CATEGORIES_AGENDA_WILL_MOUNT,
            ACCOUNT_SELECTED,
            REFETCH_DATA,
            SEND_CSV_FILE_SUCCESS,
            MENU_DATA_INVALIDATED
        ),
        filter((action: Action) => shouldRefetchMenuDataEntity(action, "itemsList")),
        filter(() => shouldDownloadItemsAndCategoriesData(deps.getState())),
        map(() => createLoadingStarted("itemsAndCategories"))
    )

export const fetchDataOnMountEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(
            ITEMS_CATEGORIES_AGENDA_WILL_MOUNT,
            ACCOUNT_SELECTED,
            REFETCH_DATA,
            LOCATION_SELECTED,
            SEND_CSV_FILE_SUCCESS,
            MENU_DATA_INVALIDATED
        ),
        delayWhen(action => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        filter((action: Action) => shouldRefetchMenuDataEntity(action, "itemsList")),
        filter(
            (action: Action) =>
                (shouldDownloadItemsAndCategoriesData(deps.getState()) && action.type !== LOCATION_SELECTED) ||
                // todo: refactor this and get rid of epics for schematized agenda in general
                (isSchematizedAgendaOpened(deps.getState()) && action.type === LOCATION_SELECTED)
        ),
        switchMap(() => {
            const locationId = isSchematizedAgendaOpened(deps.getState())
                ? getSelectedLocationId(deps.getState())
                : getSelectedLocationOverrideId(deps.getState())

            return defer(() => fetchItemsAndCategoriesListData(getHttpHeaders(deps.getState()), locationId)).pipe(
                switchMap(result => {
                    if (!result) {
                        return [createLoadingEnded("itemsAndCategories")]
                    }

                    return [
                        createLoadingEnded("itemsAndCategories"),
                        itemsAndCategoriesListDataLoaded(
                            {
                                data: result.data,
                                overrides: result.overrides,
                            },
                            locationId
                        ),
                    ]
                }),
                catchError(() => of(createLoadingEndedError("itemsAndCategories")))
            )
        })
    )

export const fetchCategoriesOnLocationLoadingEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOCATION_OVERRIDE_SELECTED),
        filter(
            (action: Action) =>
                action.payload?.locationId &&
                shouldDownloadItemsAndCategoriesData(deps.getState()) &&
                shouldDownloadLocationOverrideData(deps.getState())
        ),
        switchMap(() => {
            return [createLoadingStarted("itemsAndCategories")]
        })
    )

export const fetchCategoriesOnLocationSelectEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOCATION_OVERRIDE_SELECTED, REFETCH_DATA),
        filter(() => shouldDownloadItemsAndCategoriesData(deps.getState())),
        switchMap(() => {
            //const locationId = action.payload?.locationId
            const locationId = getSelectedLocationOverrideId(deps.getState())
            if (!locationId) {
                // Master list selected, no need to fetch data
                return [createLoadingEnded("itemsAndCategories")]
            }
            if (!shouldDownloadLocationOverrideData(deps.getState())) {
                return [createLoadingEnded("itemsAndCategories")]
            }
            return defer(() => fetchItemsAndCategoriesListData(getHttpHeaders(deps.getState()), locationId)).pipe(
                switchMap(result => {
                    if (!result) {
                        return [createLoadingEnded("itemsAndCategories")]
                    }

                    return [
                        createLoadingEnded("itemsAndCategories"),
                        itemsAndCategoriesListOverrideDataLoaded(result.overrides ?? [], locationId),
                    ]
                }),
                catchError(() => of(createLoadingEndedError("itemsAndCategories")))
            )
        })
    )

export const fetchCategoriesOnRequestEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ITEMS_AND_CATEGORIES_LIST_REQUESTED),
        switchMap((action: Action) => {
            const locationId = action.payload?.locationId ?? null
            return concat(
                of(createLoadingStarted("itemsAndCategories")),
                defer(() => fetchItemsAndCategoriesListData(getHttpHeaders(deps.getState()), locationId)).pipe(
                    switchMap(result => {
                        if (!result) {
                            return [createLoadingEnded("itemsAndCategories")]
                        }

                        return [
                            createLoadingEnded("itemsAndCategories"),
                            itemsAndCategoriesListDataLoaded(
                                {
                                    data: result.data,
                                    overrides: result.overrides,
                                },
                                locationId
                            ),
                        ]
                    }),
                    catchError(() => of(createLoadingEndedError("itemsAndCategories")))
                )
            )
        })
    )
export default [
    startLoadingOnMountEpics,
    fetchDataOnMountEpics,
    fetchCategoriesOnLocationSelectEpics,
    fetchCategoriesOnLocationLoadingEpics,
    fetchCategoriesOnRequestEpics,
]
